import { AlertCircle, Check, Layers, Pencil, RefreshCw, Trash2, Upload, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ApiError } from "@/lib/api/client";
import type { RequestOut } from "@/lib/api/requests";
import { cn } from "@/lib/cn";

import StatusBadge from "../jobs/StatusBadge";
import { formatDateTime, progressPercent, useRelativeTime } from "../jobs/format";
import { useDeleteRequest, useRequests, useUpdateRequest } from "./useRequests";

export default function RequestsListPage() {
  const { t } = useTranslation();
  const formatRelative = useRelativeTime();
  const { data: requests, isLoading, isError, error, refetch, isFetching } = useRequests();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const updateMutation = useUpdateRequest();
  const deleteMutation = useDeleteRequest();

  const startEdit = (req: RequestOut) => {
    setEditingId(req.id);
    setEditingTitle(req.title ?? "");
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingTitle("");
  };
  const saveEdit = (requestId: string) => {
    const next = editingTitle.trim();
    updateMutation.mutate(
      { requestId, title: next.length > 0 ? next : null },
      { onSuccess: cancelEdit },
    );
  };

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{t("requests.list.title")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("requests.list.description")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} />
            {t("common.actions.refresh")}
          </button>
          <Link
            to="/upload"
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow transition hover:bg-blue-700"
          >
            <Upload className="h-3.5 w-3.5" />
            {t("requests.list.newUpload")}
          </Link>
        </div>
      </header>

      {isLoading ? (
        <p className="text-sm text-slate-400">{t("common.loading")}</p>
      ) : isError ? (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{error instanceof ApiError ? error.detail : (error as Error).message}</span>
        </div>
      ) : !requests || requests.length === 0 ? (
        <EmptyState />
      ) : (
        <ul className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          {requests.map((req, idx) => {
            const pct = progressPercent(
              req.file_count,
              req.success_count,
              req.rejected_count,
              req.error_count,
            );
            return (
              <li
                key={req.id}
                className={cn(
                  "group relative transition hover:bg-slate-50",
                  idx > 0 && "border-t border-slate-100",
                )}
              >
                <Link
                  to={`/requests/${req.id}`}
                  aria-label={req.title || req.id.slice(0, 8)}
                  className="absolute inset-0 z-0"
                />
                <div className="pointer-events-none relative z-10 grid grid-cols-12 items-center gap-3 py-3 pl-4 pr-12 text-sm">
                  <div className="col-span-4 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="truncate font-medium text-slate-900">
                        {req.title || (
                          <span className="text-slate-400">
                            {t("common.placeholders.anonymous")}
                          </span>
                        )}
                      </p>
                      {editingId !== req.id && (
                        <button
                          type="button"
                          onClick={() => startEdit(req)}
                          className="pointer-events-auto shrink-0 rounded p-1 text-slate-300 opacity-0 transition hover:bg-blue-50 hover:text-blue-600 focus:opacity-100 group-hover:opacity-100"
                          aria-label={t("requests.list.renameAria", {
                            name: req.title || req.id.slice(0, 8),
                          })}
                          title={t("requests.list.renameTitle")}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <p className="mt-0.5 truncate text-[11px] text-slate-400">
                      {t("requests.list.jobsFiles", {
                        jobs: req.job_count,
                        files: req.file_count,
                      })}
                    </p>
                  </div>

                  <div className="col-span-3 flex items-center gap-2">
                    <StatusBadge status={req.status} />
                    {req.status === "completed" && (
                      <span
                        className={cn(
                          "rounded px-1.5 py-0.5 text-[11px]",
                          req.reviewed_count >= req.file_count && req.file_count > 0
                            ? "bg-blue-100 text-blue-800"
                            : "bg-slate-100 text-slate-600",
                        )}
                        title={t("requests.list.reviewedTitle")}
                      >
                        {t("requests.list.reviewedCount", {
                          reviewed: req.reviewed_count,
                          total: req.file_count,
                        })}
                      </span>
                    )}
                  </div>

                  <div className="col-span-3">
                    {req.status === "completed" ? (
                      <div className="flex items-center gap-1.5 text-[11px]">
                        {req.success_count > 0 && (
                          <span className="rounded bg-green-50 px-1.5 py-0.5 text-green-700">
                            ✓ {req.success_count}
                          </span>
                        )}
                        {req.rejected_count > 0 && (
                          <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-700">
                            ⚠ {req.rejected_count}
                          </span>
                        )}
                        {req.error_count > 0 && (
                          <span className="rounded bg-red-50 px-1.5 py-0.5 text-red-700">
                            ✗ {req.error_count}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div>
                        <div className="mb-1 flex justify-between text-[10px] text-slate-400">
                          <span>{pct}%</span>
                          <span>
                            {req.done_jobs}/{req.job_count}
                          </span>
                        </div>
                        <div className="h-1 overflow-hidden rounded-full bg-slate-100">
                          <div
                            className="h-full bg-blue-500 transition-[width] duration-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="col-span-2 text-right">
                    <p className="text-xs text-slate-600" title={formatDateTime(req.created_at)}>
                      {formatRelative(req.created_at)}
                    </p>
                  </div>
                </div>

                {editingId === req.id && (
                  <div className="absolute left-4 top-1/2 z-20 flex w-1/3 min-w-[240px] -translate-y-1/2 items-center gap-1.5">
                    <input
                      autoFocus
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          saveEdit(req.id);
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          cancelEdit();
                        }
                      }}
                      placeholder={t("requests.list.renamePlaceholder")}
                      maxLength={255}
                      disabled={updateMutation.isPending}
                      className="w-full rounded-md border border-blue-400 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-blue-200"
                    />
                    <button
                      type="button"
                      onClick={() => saveEdit(req.id)}
                      disabled={updateMutation.isPending}
                      className="rounded p-1.5 text-emerald-600 transition hover:bg-emerald-50 disabled:opacity-50"
                      aria-label={t("common.actions.save")}
                    >
                      <Check className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      disabled={updateMutation.isPending}
                      className="rounded p-1.5 text-slate-400 transition hover:bg-slate-100 disabled:opacity-50"
                      aria-label={t("common.actions.cancel")}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(t("requests.list.deleteConfirm"))) {
                      deleteMutation.mutate(req.id);
                    }
                  }}
                  className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded p-1.5 text-slate-400 opacity-0 transition hover:bg-red-50 hover:text-red-600 focus:opacity-100 group-hover:opacity-100"
                  aria-label={t("requests.list.deleteAria", {
                    name: req.title || req.id.slice(0, 8),
                  })}
                  title={t("requests.list.deleteTitle")}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-white py-16 text-center">
      <Layers className="mx-auto h-10 w-10 text-slate-300" />
      <p className="mt-3 text-sm font-medium text-slate-700">{t("requests.list.empty.title")}</p>
      <p className="mt-1 text-xs text-slate-400">{t("requests.list.empty.description")}</p>
      <Link
        to="/upload"
        className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow hover:bg-blue-700"
      >
        <Upload className="h-3.5 w-3.5" />
        {t("requests.list.empty.action")}
      </Link>
    </div>
  );
}
